/**
 * Search Results AG Grid Component
 *
 * Displays mixed-entity search results (Work Packages + Work Items) in AG Grid.
 * Columns: Type, Title, Description, Category, Status, Due Date, Tags.
 */

import { useMemo, useRef, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type {
  ColDef,
  GridReadyEvent,
  GridApi,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './blueprint-grid.css';

import type { ProcessTemplate, ProcessInstance, ProcessStatus } from '@/types';
import { useTheme } from '@contexts/ThemeContext';

export interface SearchResultRow {
  id: string;
  type: 'template' | 'instance';
  typeLabel: string;
  title: string;
  description: string;
  category: string;
  status: string;
  dueDate: string;
  tags: string;
  priority: number;
  raw: ProcessTemplate | ProcessInstance;
}

interface SearchResultsGridProps {
  templates: ProcessTemplate[];
  instances: ProcessInstance[];
  query: string;
  loading?: boolean;
  onRowClick: (row: SearchResultRow) => void;
}

const statusLabels: Record<ProcessStatus, string> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  completed: 'Completed',
  postponed: 'Postponed',
  delayed: 'Delayed',
  cancelled: 'Cancelled',
  ignored: 'Ignored',
};

export default function SearchResultsGrid({
  templates,
  instances,
  query,
  onRowClick,
}: SearchResultsGridProps) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.isDark ?? false;
  const gridRef = useRef<GridApi | null>(null);

  const rows: SearchResultRow[] = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    const results: SearchResultRow[] = [];

    // Search templates
    templates.forEach((t) => {
      if (
        t.title.toLowerCase().includes(q) ||
        t.description?.toLowerCase().includes(q)
      ) {
        results.push({
          id: t.id,
          type: 'template',
          typeLabel: 'Work Package',
          title: t.title,
          description: t.description || '',
          category: t.category?.name || '',
          status: t.isActive ? 'Active' : 'Inactive',
          dueDate: '',
          tags: t.tags?.map((tag) => tag.name).join(', ') || '',
          priority: 0,
          raw: t,
        });
      }
    });

    // Search instances
    instances.forEach((i) => {
      const title = i.title || i.template?.title || '';
      if (
        title.toLowerCase().includes(q) ||
        i.completionNotes?.toLowerCase().includes(q)
      ) {
        results.push({
          id: i.id,
          type: 'instance',
          typeLabel: 'Work Item',
          title,
          description: i.completionNotes || '',
          category: i.template?.category?.name || '',
          status: statusLabels[i.status] || i.status,
          dueDate: i.dueDate
            ? new Date(i.dueDate).toLocaleDateString()
            : '',
          tags: i.tags?.map((tag) => tag.name).join(', ') || '',
          priority: i.priority || 0,
          raw: i,
        });
      }
    });

    return results;
  }, [templates, instances, query]);

  const columnDefs: ColDef<SearchResultRow>[] = useMemo(
    () => [
      {
        field: 'typeLabel',
        headerName: 'Type',
        width: 130,
        cellRenderer: (params: import('ag-grid-community').ICellRendererParams<SearchResultRow>) => (
          <span className={`bp-status-chip ${params.data?.type === 'template' ? 'bp-status-in_progress' : 'bp-status-completed'}`}>
            {params.value}
          </span>
        ),
      },
      {
        field: 'title',
        headerName: 'Title',
        flex: 2,
        minWidth: 200,
        cellStyle: () => ({ fontWeight: 500 }),
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 1,
        minWidth: 150,
        cellStyle: () => ({ color: 'var(--text-secondary)', fontSize: '12px' }),
      },
      {
        field: 'category',
        headerName: 'Category',
        width: 150,
        cellStyle: () => ({ color: 'var(--text-secondary)' }),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 130,
        cellRenderer: (params: import('ag-grid-community').ICellRendererParams<SearchResultRow>) => {
          if (!params.value) return null;
          // Map display label back to a chip class
          const classMap: Record<string, string> = {
            'Pending': 'bp-status-pending',
            'In Progress': 'bp-status-in_progress',
            'Completed': 'bp-status-completed',
            'Postponed': 'bp-status-postponed',
            'Delayed': 'bp-status-delayed',
            'Cancelled': 'bp-status-cancelled',
            'Ignored': 'bp-status-ignored',
            'Active': 'bp-status-active',
            'Inactive': 'bp-status-inactive',
          };
          const cls = classMap[params.value] || 'bp-status-pending';
          return <span className={`bp-status-chip ${cls}`}>{params.value}</span>;
        },
      },
      {
        field: 'dueDate',
        headerName: 'Due Date',
        width: 120,
        cellRenderer: (params: import('ag-grid-community').ICellRendererParams<SearchResultRow>) => {
          if (!params.value) return <span style={{ color: 'var(--text-tertiary)' }}>—</span>;
          return <span className="bp-mono">{params.value}</span>;
        },
      },
      {
        field: 'tags',
        headerName: 'Tags',
        width: 200,
        cellStyle: () => ({ color: 'var(--text-secondary)', fontSize: '12px', fontFamily: 'var(--font-mono)' }),
      },
    ],
    []
  );

  const defaultColDef: ColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
    }),
    []
  );

  const onGridReady = useCallback((params: GridReadyEvent) => {
    gridRef.current = params.api;
    params.api.sizeColumnsToFit();
  }, []);

  return (
    <div
      className={`${isDark ? 'ag-theme-alpine-dark' : 'ag-theme-alpine'} bp-grid`}
      style={{
        width: '100%',
        height: '100%',
        minHeight: 400,
        ...({
          '--ag-background-color': 'var(--surface-1)',
          '--ag-odd-row-background-color': 'var(--row-alt, #f7f9fc)',
          '--ag-row-border-color': 'var(--border-subtle)',
          '--ag-header-background-color': 'var(--surface-1)',
          '--ag-border-color': 'var(--grid-line, #dde3ec)',
          '--ag-foreground-color': 'var(--text-primary)',
          '--ag-row-hover-color': 'var(--hover-bg)',
          '--ag-selected-row-background-color': 'rgba(30, 64, 175, 0.10)',
          '--ag-header-foreground-color': 'var(--text-tertiary)',
        }),
      } as React.CSSProperties}
    >
      <AgGridReact<SearchResultRow>
        rowData={rows}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        onGridReady={onGridReady}
        onRowClicked={(e) => e.data && onRowClick(e.data)}
        rowSelection="single"
        animateRows
        overlayLoadingTemplate={
          '<span style="padding: 24px; font-size: 14px; color: #888;">Loading...</span>'
        }
        overlayNoRowsTemplate={
          query.trim()
            ? '<span style="padding: 24px; font-size: 14px; color: #888;">No results found</span>'
            : '<span style="padding: 24px; font-size: 14px; color: #888;">Enter a search query to see results</span>'
        }
      />
    </div>
  );
}
